import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, Mic, Send, Volume2, Sparkles, Loader2, CheckCircle2, Lock, MessageSquare 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { dbManager } from '../services/db_manager';
import { supabase } from '../services/supabaseClient';
import { BonusTopicDef, TARGET_LANG_NAMES, FAREWELL_INSTRUCTIONS } from '../constants/roadmapBonusData';
import T from './T';

interface BonusActiveVoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  level: string; // e.g. 'a1', 'b2'
  targetLangCode: string; // e.g. 'en', 'fr', 'de'
  nativeLangCode: string; // e.g. 'pt', 'en'
  bonusDef: BonusTopicDef;
}

export const BonusActiveVoiceModal: React.FC<BonusActiveVoiceModalProps> = ({
  isOpen,
  onClose,
  level,
  targetLangCode,
  nativeLangCode,
  bonusDef
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const cleanLang = (targetLangCode || 'en').split('-')[0].toLowerCase();
  const cleanLevel = (level || 'a1').toLowerCase();
  const cleanNative = (nativeLangCode || 'pt').split('-')[0].toLowerCase();

  // Unified Session ID for this exact Bonus Topic & User
  const sessionId = `roadmap_bonus_${cleanLang}_${cleanLevel}_b${bonusDef.bonusNumber}_${user?.id || 'guest'}`;

  const [messages, setMessages] = useState<Array<{ id: string; role: 'user' | 'assistant'; content: string; created_at: string }>>([]);
  const [textInput, setTextInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const targetLangName = TARGET_LANG_NAMES[cleanLang]?.[cleanNative] || TARGET_LANG_NAMES[cleanLang]?.pt || 'Inglês';

  // Target speech recognition language mapping
  const SPEECH_LANG_MAP: Record<string, string> = {
    en: 'en-US', fr: 'fr-FR', de: 'de-DE', es: 'es-ES', it: 'it-IT', hu: 'hu-HU', ru: 'ru-RU', pt: 'pt-BR'
  };
  const speechLangCode = SPEECH_LANG_MAP[cleanLang] || 'en-US';

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing]);

  // Load Session History or Trigger Initial Greeting
  useEffect(() => {
    if (!isOpen || !user?.id) return;

    let isMounted = true;
    setIsLoadingHistory(true);
    setIsCompleted(false);

    const loadSession = async () => {
      try {
        const res = await dbManager.execute({
          action: 'select',
          table: 'active_voice_messages',
          user_id: user.id,
          filters: { session_id: sessionId }
        });

        const list = Array.isArray(res) ? res : (res?.data || []);
        if (!isMounted) return;

        if (list && list.length > 0) {
          // Sort messages chronologically
          const sorted = list
            .map((m: any) => ({
              id: m.id || String(Math.random()),
              role: (m.role === 'assistant' ? 'assistant' : 'user') as 'user' | 'assistant',
              content: m.content || m.text || '',
              created_at: m.created_at || new Date().toISOString()
            }))
            .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

          setMessages(sorted);

          const assistantCount = sorted.filter((m: any) => m.role === 'assistant').length;
          if (assistantCount >= 5) {
            setIsCompleted(true);
          }
          setIsLoadingHistory(false);
        } else {
          // 0 messages: Generate Initial AI Greeting in Target Language
          setMessages([]);
          setIsLoadingHistory(false);
          await generateInitialGreeting();
        }
      } catch (err) {
        console.error("[BonusVoiceModal] Error loading history:", err);
        if (isMounted) setIsLoadingHistory(false);
      }
    };

    loadSession();

    return () => {
      isMounted = false;
      stopTTS();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, [isOpen, sessionId, user?.id]);

  // TTS audio playback
  const stopTTS = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
  };

  const playTTS = async (text: string) => {
    stopTTS();
    if (!text || !text.trim()) return;

    const cleanText = text.replace(/\[LANG:\s*[a-zA-Z-]+\]/gi, '').trim().substring(0, 200);
    try {
      const { textToSpeechService } = await import('../services/textToSpeechService');
      await textToSpeechService.speak(cleanText, speechLangCode, 0.85);
    } catch (e) {
      console.warn("TTS failed:", e);
    }
  };

  // Generate Initial AI Greeting
  const generateInitialGreeting = async () => {
    if (!user?.id || isProcessing) return;
    setIsProcessing(true);

    try {
      const specificInstruction = bonusDef.getGroqInstruction(targetLangName);
      const systemPrompt = `Você é o Professor LibriTui, tutor do idioma ${targetLangName}.

${specificInstruction}

REGRA DE IDIOMA ESTRITA (MUITO IMPORTANTE): Você DEVE OBRIGATORIAMENTE iniciar a conversa e responder APENAS E EXCLUSIVAMENTE em ${targetLangName}. NUNCA use o idioma nativo do aluno (${cleanNative}).
REGRA DE TAMANHO: A mensagem deve ser curta, amigável e ter no MÁXIMO 300 caracteres.
TAG DO SINTETIZADOR DE VOZ: A PRIMEIRA LINHA da sua resposta DEVE SER exatamente [LANG:${cleanLang}].`;

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/ai-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-specdec',
          source: 'active-voice',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: 'Inicie nossa conversa para este tópico bônus.' }
          ],
          max_tokens: 120
        })
      });

      if (!res.ok) throw new Error("Erro na API da IA");

      const data = await res.json();
      let aiRaw = data.choices?.[0]?.message?.content || 'Olá! Vamos conversar.';
      let cleanText = aiRaw.replace(/\[LANG:\s*[a-zA-Z-]+\]/gi, '').trim().slice(0, 300);

      const generatedId = `msg-init-${Date.now().toString(36)}`;
      const newMsg = {
        id: generatedId,
        role: 'assistant' as const,
        content: cleanText,
        created_at: new Date().toISOString()
      };

      setMessages([newMsg]);
      playTTS(cleanText);

      // Save Initial AI Message to D1
      await dbManager.from('active_voice_messages', user.id).insert({
        id: generatedId,
        session_id: sessionId,
        user_id: user.id,
        role: 'assistant',
        content: cleanText,
        text: cleanText,
        lang: cleanLang,
        created_at: new Date().toISOString()
      }).catch(() => {});

      // Upsert Session in D1
      await dbManager.execute({
        action: 'increment_voice_session',
        table: 'active_voice_sessions',
        user_id: user.id,
        data: { seconds: 10, language_spoken: cleanLang, session_id: sessionId }
      }).catch(() => {});

    } catch (e) {
      console.error("[BonusVoiceModal] Erro ao iniciar saudação:", e);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle User Message Send
  const handleSendMessage = async (textToSend?: string) => {
    let finalInput = typeof textToSend === 'string' ? textToSend : textInput;
    if (!finalInput.trim() || isProcessing || isCompleted || !user?.id) return;

    // Trava de tamanho 300 caracteres max
    const userText = finalInput.trim().slice(0, 300);
    setTextInput('');
    stopTTS();

    const userMsgId = `usr-${Date.now().toString(36)}`;
    const userMsg = {
      id: userMsgId,
      role: 'user' as const,
      content: userText,
      created_at: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setIsProcessing(true);

    // Save User Message to D1
    dbManager.from('active_voice_messages', user.id).insert({
      id: userMsgId,
      session_id: sessionId,
      user_id: user.id,
      role: 'user',
      content: userText,
      text: userText,
      lang: cleanLang,
      created_at: new Date().toISOString()
    }).catch(() => {});

    // Count user messages in conversation
    const userMsgCount = updatedMessages.filter(m => m.role === 'user').length;
    const isFifthTurn = userMsgCount >= 4;

    try {
      const specificInstruction = bonusDef.getGroqInstruction(targetLangName);
      const farewellRule = FAREWELL_INSTRUCTIONS[cleanLang] || FAREWELL_INSTRUCTIONS['en'];
      const systemPrompt = `Você é o Professor LibriTui, tutor do idioma ${targetLangName}.

${specificInstruction}

REGRA DE IDIOMA ESTRITA (MUITO IMPORTANTE): Você DEVE OBRIGATORIAMENTE responder APENAS E EXCLUSIVAMENTE no idioma de estudo (${targetLangName}). NUNCA responda em português ou no idioma nativo (${cleanNative}).
REGRA DE TAMANHO RIGOROSA: Suas respostas DEVEM TER NO MÁXIMO 300 CARACTERES.

${isFifthTurn ? farewellRule : ''}

TAG DO SINTETIZADOR DE VOZ: A PRIMEIRA LINHA da sua resposta DEVE SER exatamente [LANG:${cleanLang}].`;

      const recentHistory = updatedMessages.slice(-6).map(m => ({
        role: m.role,
        content: m.content
      }));

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/ai-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-specdec',
          source: 'active-voice',
          messages: [
            { role: 'system', content: systemPrompt },
            ...recentHistory
          ],
          max_tokens: 150
        })
      });

      if (!res.ok) throw new Error("Erro na API da IA");

      const data = await res.json();
      let aiRaw = data.choices?.[0]?.message?.content || 'Muito bem! Continue praticando.';
      let cleanText = aiRaw.replace(/\[LANG:\s*[a-zA-Z-]+\]/gi, '').trim().slice(0, 300);

      const aiMsgId = `ai-${Date.now().toString(36)}`;
      const aiMsg = {
        id: aiMsgId,
        role: 'assistant' as const,
        content: cleanText,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMsg]);
      playTTS(cleanText);

      // Save AI Message to D1
      dbManager.from('active_voice_messages', user.id).insert({
        id: aiMsgId,
        session_id: sessionId,
        user_id: user.id,
        role: 'assistant',
        content: cleanText,
        text: cleanText,
        lang: cleanLang,
        created_at: new Date().toISOString()
      }).catch(() => {});

      // Increment Voice Session D1 Duration
      dbManager.execute({
        action: 'increment_voice_session',
        table: 'active_voice_sessions',
        user_id: user.id,
        data: { seconds: 20, language_spoken: cleanLang, session_id: sessionId }
      }).catch(() => {});

      // Check for completion (5 assistant responses)
      const assistantCount = updatedMessages.filter(m => m.role === 'assistant').length + 1;
      if (assistantCount >= 5 || isFifthTurn) {
        setIsCompleted(true);
      }

    } catch (e) {
      console.error("[BonusVoiceModal] Erro ao enviar mensagem:", e);
    } finally {
      setIsProcessing(false);
    }
  };

  // Toggle Speech Recognition
  const toggleListening = () => {
    if (isCompleted || isProcessing) return;

    if (isListening) {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      setIsListening(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Seu navegador não suporta reconhecimento de voz.");
      return;
    }

    stopTTS();
    const recognition = new SpeechRecognition();
    recognition.lang = speechLangCode;
    recognition.interimResults = false;
    recognition.continuous = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e: any) => {
      const transcript = e.results?.[0]?.[0]?.transcript || '';
      if (transcript.trim()) {
        handleSendMessage(transcript.trim());
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
  };

  if (!isOpen) return null;

  const topicTitle = bonusDef.title[cleanNative] || bonusDef.title['pt'] || bonusDef.title['en'];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="bg-surface rounded-[28px] w-full max-w-xl flex flex-col h-[85vh] max-h-[720px] overflow-hidden border border-black/[0.06] dark:border-white/[0.06]"
      >
        {/* Header */}
        <div className="p-5 border-b border-black/[0.06] flex items-center justify-between bg-gradient-to-r from-warn/10 via-tintd-lavender/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-warn via-bad to-tintd-lavender p-0.5">
              <div className="w-full h-full bg-ink rounded-[14px] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-warn" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-tint-butter dark:bg-tint-butter text-warn dark:text-warn border border-tint-butter/40">
                  Voz Ativa PRO • Nível {level.toUpperCase()}
                </span>
              </div>
              <h3 className="text-base font-semibold text-ink leading-tight mt-0.5">
                {topicTitle}
              </h3>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2.5 text-muted hover:text-muted bg-tint-stone/60 rounded-2xl transition-all cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Chat Messages Body */}
        <div className="flex-1 p-5 overflow-y-auto space-y-4 bg-tint-stone/50">
          {isLoadingHistory ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted">
              <Loader2 className="w-8 h-8 animate-spin text-warn" />
              <span className="text-xs font-semibold">Carregando conversa...</span>
            </div>
          ) : messages.length === 0 && !isProcessing ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-muted text-center px-6">
              <MessageSquare className="w-10 h-10 text-warn opacity-60" />
              <p className="text-xs font-medium"><T k="ui.bonus.starting" /></p>
            </div>
          ) : (
            <>
              {messages.map((m) => {
                const isAI = m.role === 'assistant';
                return (
                  <div
                    key={m.id}
                    className={`flex flex-col ${isAI ? 'items-start' : 'items-end'}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 px-1">
                      <span className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                        {isAI ? 'Professor LibriTui' : 'Você'}
                      </span>
                    </div>
                    <div
                      className={`max-w-[85%] p-4 rounded-3xl text-xs sm:text-sm font-medium leading-relaxed ${
                        isAI
                          ? 'bg-surface text-ink rounded-tl-sm border border-black/[0.06] '
                          : 'bg-gradient-to-r from-ok to-ok text-white rounded-tr-sm'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                );
              })}

              {isProcessing && (
                <div className="flex flex-col items-start">
                  <div className="flex items-center gap-2 p-4 rounded-3xl rounded-tl-sm bg-surface border border-black/[0.06]">
                    <Loader2 className="w-4 h-4 animate-spin text-warn" />
                    <span className="text-xs font-medium text-muted"><T k="ui.bonus.typing" /></span>
                  </div>
                </div>
              )}

              {isCompleted && (
                <div className="my-4 p-4 rounded-2xl bg-warn/10 border border-warn/30 text-warn dark:text-warn flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 shrink-0 text-warn" />
                  <div>
                    <h5 className="text-xs font-normal uppercase font-mono tracking-[0.14em]"><T k="ui.bonus.done" /></h5>
                    <p className="text-[11px] font-medium opacity-90 mt-0.5">
                      Você completou todas as 5 interações deste tópico bônus com sucesso.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Controls */}
        <div className="p-4 border-t border-black/[0.06] dark:border-white/[0.06] bg-surface">
          {isCompleted ? (
            <div className="py-3 px-4 rounded-2xl bg-tint-stone/60 text-center text-xs font-bold text-muted">
              Sessão bônus finalizada. Parabéns pelo excelente treino de conversa!
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* Mic Button */}
              <button
                type="button"
                onClick={toggleListening}
                disabled={isProcessing}
                className={`p-3.5 rounded-2xl transition-all cursor-pointer flex items-center justify-center shrink-0 ${
                  isListening
                    ? 'bg-bad text-surface animate-pulse ring-4 ring-tint-rose/50'
                    : 'bg-tint-stone/60 text-muted hover:bg-tint-stone '
                }`}
                title="Falar no Microfone"
              >
                <Mic size={20} />
              </button>

              {/* Text Input (Max 300 Chars) */}
              <div className="relative flex-1">
                <input
                  type="text"
                  maxLength={300}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value.slice(0, 300))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={isListening ? "Ouvindo sua fala..." : "Digite sua mensagem (máx. 300 caracteres)..."}
                  disabled={isProcessing}
                  className="w-full py-3.5 pl-4 pr-12 text-xs sm:text-sm font-medium bg-tint-stone/60 text-ink rounded-2xl border border-transparent focus:border-warn dark:focus:border-warn focus:outline-none transition-all"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted pointer-events-none">
                  {textInput.length}/300
                </span>
              </div>

              {/* Send Button */}
              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={!textInput.trim() || isProcessing}
                className="p-3.5 bg-gradient-to-r from-warn via-bad to-tintd-lavender text-white rounded-2xl hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100 transition-all cursor-pointer flex items-center justify-center shrink-0"
              >
                <Send size={18} />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BonusActiveVoiceModal;
